import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/common/PageHeader'
import { StatusBadge } from '@/components/common/StatusBadge'
import { NotesTimeline } from '@/components/common/NotesTimeline'
import type { Tables, Enums } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

type Order = Tables<'orders'>
type OrderLineItem = Tables<'order_line_items'>
type OrderStatusHistory = Tables<'order_status_history'>

const MILESTONES: Enums<'order_status'>[] = ['received', 'validated', 'acknowledged', 'in_production', 'ready_to_ship', 'shipped', 'delivered']

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { profile } = useAuth()
  const [order, setOrder] = useState<Order | null>(null)
  const [lineItems, setLineItems] = useState<OrderLineItem[]>([])
  const [statusHistory, setStatusHistory] = useState<OrderStatusHistory[]>([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState(false)

  useEffect(() => {
    if (!id) return
    async function load() {
      const [orderRes, linesRes, historyRes] = await Promise.all([
        supabase.from('orders').select('*').eq('id', id!).single(),
        supabase.from('order_line_items').select('*').eq('order_id', id!).order('line_number'),
        supabase.from('order_status_history').select('*').eq('order_id', id!).order('created_at', { ascending: true }),
      ])
      setOrder(orderRes.data)
      setLineItems(linesRes.data || [])
      setStatusHistory(historyRes.data || [])
      setLoading(false)
    }
    load()
  }, [id])

  const advanceStatus = async () => {
    if (!order || !profile) return
    const currentIdx = MILESTONES.indexOf(order.status as Enums<'order_status'>)
    if (currentIdx < 0 || currentIdx >= MILESTONES.length - 1) return
    const nextStatus = MILESTONES[currentIdx + 1]

    setUpdating(true)
    await supabase.from('orders').update({ status: nextStatus }).eq('id', order.id)
    await supabase.from('order_status_history').insert({
      order_id: order.id,
      status: nextStatus,
      changed_by: profile.id,
      notes: `Status advanced to ${nextStatus.replace(/_/g, ' ')}`,
    })
    await supabase.from('activity_log').insert({
      organization_id: order.organization_id,
      project_id: order.project_id,
      actor_id: profile.id,
      activity_type: 'order_status_changed',
      entity_type: 'order',
      entity_id: order.id,
      summary: `Order ${order.reference_code} status changed to ${nextStatus.replace(/_/g, ' ')}`,
    })
    setOrder({ ...order, status: nextStatus })
    setStatusHistory([...statusHistory, { id: crypto.randomUUID(), order_id: order.id, status: nextStatus, changed_by: profile.id, notes: null, metadata: null, created_at: new Date().toISOString() }])
    setUpdating(false)
  }

  if (loading) return <p style={{ color: 'var(--color-text-muted)', padding: 'var(--space-8)' }}>Loading order...</p>
  if (!order) return <p>Order not found</p>

  const currentIdx = MILESTONES.indexOf(order.status as Enums<'order_status'>)
  const canAdvance = profile && ['operations', 'management', 'partner_admin', 'keder_admin'].includes(profile.role) && currentIdx < MILESTONES.length - 1 && order.status !== 'cancelled'

  return (
    <div>
      <PageHeader
        title={`Order ${order.reference_code}`}
        description={`Created ${new Date(order.created_at).toLocaleDateString()}${order.po_number ? ` · PO: ${order.po_number}` : ''}`}
        actions={canAdvance ? (
          <button className={css.btnPrimary} onClick={advanceStatus} disabled={updating}>
            {updating ? 'Updating...' : `Advance to ${MILESTONES[currentIdx + 1]?.replace(/_/g, ' ')}`}
          </button>
        ) : undefined}
      />

      {/* Status Timeline */}
      <div className={css.card} style={{ marginBottom: 'var(--space-6)' }}>
        <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)' }}>Order Timeline</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, position: 'relative', padding: '0 var(--space-2)' }}>
          {MILESTONES.map((milestone, i) => {
            const isCompleted = i <= currentIdx
            const isCurrent = i === currentIdx
            const historyEntry = statusHistory.find(h => h.status === milestone)
            return (
              <div key={milestone} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
                {i > 0 && (
                  <div style={{
                    position: 'absolute', top: 12, right: '50%', width: '100%', height: 3,
                    background: isCompleted ? 'var(--color-primary)' : 'var(--color-border)',
                    zIndex: 0,
                  }} />
                )}
                <div style={{
                  width: isCurrent ? 28 : 24, height: isCurrent ? 28 : 24,
                  borderRadius: '50%',
                  background: isCompleted ? 'var(--color-primary)' : 'var(--color-bg-elevated)',
                  border: isCompleted ? 'none' : '2px solid var(--color-border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  zIndex: 1, position: 'relative',
                  boxShadow: isCurrent ? '0 0 0 4px var(--color-primary-muted)' : 'none',
                }}>
                  {isCompleted && (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                  )}
                </div>
                <span style={{
                  fontSize: 'var(--text-xs)', marginTop: 'var(--space-2)',
                  color: isCompleted ? 'var(--color-text)' : 'var(--color-text-muted)',
                  fontWeight: isCurrent ? 600 : 400,
                  textTransform: 'capitalize', textAlign: 'center',
                }}>
                  {milestone.replace(/_/g, ' ')}
                </span>
                {historyEntry && (
                  <span style={{ fontSize: '10px', color: 'var(--color-text-muted)', marginTop: 2 }}>
                    {new Date(historyEntry.created_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </div>

      <div className={css.grid2} style={{ marginBottom: 'var(--space-6)' }}>
        {/* Delivery Tracking */}
        <div className={css.card}>
          <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Delivery</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Requested</span><span>{order.requested_delivery ? new Date(order.requested_delivery).toLocaleDateString() : 'Not specified'}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Promised</span><span>{order.promised_delivery ? new Date(order.promised_delivery).toLocaleDateString() : 'Pending'}</span></div>
            {order.actual_delivery && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Actual</span><span>{new Date(order.actual_delivery).toLocaleDateString()}</span></div>}
            {order.delivery_confidence && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Confidence</span>
                <StatusBadge label={order.delivery_confidence} variant={order.delivery_confidence === 'high' ? 'success' : order.delivery_confidence === 'medium' ? 'warning' : 'error'} />
              </div>
            )}
            {order.carrier && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Carrier</span><span>{order.carrier}</span></div>}
            {order.tracking_url && <div><a href={order.tracking_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 'var(--text-sm)' }}>Track shipment</a></div>}
          </div>
        </div>

        {/* Order Summary */}
        <div className={css.card}>
          <h3 style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)', marginBottom: 'var(--space-4)' }}>Summary</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', fontSize: 'var(--text-sm)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Subtotal</span><span>£{Number(order.subtotal).toLocaleString()}</span></div>
            {Number(order.discount_amount) > 0 && <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--color-text-muted)' }}>Discount</span><span>−£{Number(order.discount_amount).toLocaleString()}</span></div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-border)' }}>
              <span style={{ fontWeight: 600, fontSize: 'var(--text-lg)' }}>Total</span>
              <span style={{ fontWeight: 700, fontSize: 'var(--text-lg)', color: 'var(--color-primary-dark)' }}>£{Number(order.total).toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Line Items */}
      <div className={css.card} style={{ padding: 0, overflow: 'hidden', marginBottom: 'var(--space-6)' }}>
        <table className={css.table}>
          <thead><tr><th>#</th><th>Description</th><th>Qty</th><th>Unit Price</th><th>Total</th></tr></thead>
          <tbody>
            {lineItems.map(item => (
              <tr key={item.id}>
                <td>{item.line_number}</td>
                <td>{item.description || 'Product'}</td>
                <td>{item.quantity}</td>
                <td>£{Number(item.unit_price).toLocaleString()}</td>
                <td style={{ fontWeight: 500 }}>£{Number(item.line_total).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Notes */}
      <NotesTimeline
        entityType="order"
        entityId={order.id}
        organizationId={order.organization_id}
        projectId={order.project_id}
      />
    </div>
  )
}
